<script lang="ts">
  import { onMount } from 'svelte';
  import { showToast } from '../lib/toast';
  import { _ } from 'svelte-i18n';
  import { getPublicClubs, signupStepTwo, trackEvent, verifyAccessGrant } from '../lib/apiService';
  import SearchableSelect from '../components/SearchableSelect.svelte';
  import { settingsStore, refreshSettings } from '../stores/settings';
  import { getHashBasedUrlParams } from '../lib/utils';
 
  
  // Define custom interface for clear type annotation
  interface SportType {
    label: string;
    value: string;
  }
  
  // Form data
  let userName = '';
  let sportsType = 'hockey'; // Default to first sport object
  let clubId = '';
  let clubName = '';
  let teamId = '';
  let teamName = '';
  let termsAccepted = false;
  
  // UI state
  let clubs = [];
  let isLoading = false;  
  let currentStep = 1; // 1 = form, 2 = success
  let isInitialized = false; // Track if component is properly initialized
  
  // Team invitation state
  let accessGrantToken = '';
  let isTeamInvitation = false;
  
  // Function to load clubs based on sports type
  async function loadClubs(type: string) {
    try {
      await getPublicClubs(type, (data, status) => {
        if (status === 200) {
          // Transform the data for SearchableSelect
          clubs = data;
        } else {
          showToast($_('signup.error'), 'error');
        }
      });
    } catch (error) {
      showToast($_('signup.error'), 'error');
      console.error(error);
    }
  }
  
  // Watch sportsType changes to reload clubs (only after initialization)
  $: if (sportsType && isInitialized && !isTeamInvitation) {
    loadClubs(sportsType);
    // Reset club selection when sports type changes
    clubId = '';
  }
  
  // Complete signup (called when form is submitted)
  async function completeSignup() {
    // Prevent multiple submissions
    if (isLoading) {
      return false;
    }

    if (!termsAccepted) {
      showToast($_('signup.terms_not_accepted'), 'error');
      return false;
    }

    if (!clubId) {
      showToast($_('signup.club_not_selected'), 'error');
      return false;
    }

    isLoading = true;
    
    try {
        const signupData = {
          sports_type: sportsType,
          club_id: clubId,
          team_name: teamName,
          username: userName,
          ...(isTeamInvitation && { access_grant_token: accessGrantToken })
        };
        
        await signupStepTwo(
        signupData,
        async (data, status) => {
          isLoading = false;

          if (status === 200) {
            // Refresh settings to get the new team
            await refreshSettings();

            trackEvent('signup_completed', {
                module: "signup",
                action_type: "account_changes",
                description: 'Complete signup process',
            }, $settingsStore.user.id);

            // Check if user has a phone number (messaging setup)
            // If no phone number, they signed up via email - redirect to teams
            if (!$settingsStore.user?.phone_number) {
              window.location.href = '/account/#/settings?tab=teams';
              return;
            }

            // Phone users see the chat prompt
            currentStep = 2;
          } else {
            if (status === 400) {
              showToast($_('signup.expired_link'), 'error', {
                link: {
                  text: $_('buttons.signup'),
                  url: '/account/#/signup',
                  onClick: () => {
                    window.location.hash = '/signup';
                  }
                }
              });
            } else {
              showToast($_('signup.error'), 'error');
            }
          }
        }
      );
    } catch (error) {
      isLoading = false;
      showToast($_('signup.error'), 'error');
    }
  }
  
  // Sport selection handler with proper typing
  function onSelectSport(event: Event) {
    const target = event.target as HTMLSelectElement;
    const selectedValue = target.value;
    
    // Find the sport object that matches the selected value
    sportsType = selectedValue;
  }

  function onSelectClub(event) {
    const { value } = event.detail;
    clubId = value;
  }

  onMount(async () => {
    await refreshSettings();

    // Check for access grant token in URL parameters
    const urlParams = getHashBasedUrlParams();
    const grantToken = urlParams.get('access_grant');
    
    if (grantToken) {
      accessGrantToken = grantToken;
      
      // Verify the access grant token to get team information
      try {
        await verifyAccessGrant(accessGrantToken, (result, status) => {
          if (status === 200 && result.is_valid) {
            const actions = result.actions;
            teamId = actions?.team_id;
            teamName = actions?.team_name;
            clubId = actions?.club_id;
            clubName = actions?.club_name;            
            if (teamId) {
              isTeamInvitation = true;
            }
          } else {
            // Invalid token, redirect to signup
            showToast($_('signup.invitation.invalid_token'), 'error');
            window.location.href = '/account/#/signup';
            return;
          }
        });
      } catch (error) {
        console.error('Error verifying access grant in SignupComplete:', error);
        showToast($_('signup.invitation.invalid.description'), 'error');
        window.location.href = '/account/#/signup';
        return;
      }
    }

    // getPublicClubs doesn't require auth, so we can load clubs immediately
    await loadClubs(sportsType);
    isInitialized = true;
  });
</script>

<div class="container">

  <header class="mt-6 mb-6">
    {#if currentStep === 1}
        <h1 class="mt-6 mb-6">{$_('signup.complete_title')}</h1>
        <p>{$_('signup.complete_subtitle')}
        {#if isTeamInvitation}<br/>
          {$_('signup.invitation.signup_up_for')}: <strong>{clubName} - {teamName}</strong>
        {/if}
        </p>
    {/if}  
  </header>
  
  {#if currentStep === 1}
    <form on:submit|preventDefault={completeSignup} class="space-y-6">
      <!-- User Name -->
      <div>
        <label for="userName">{$_('signup.user_name')}</label>
        <input 
          id="userName"
          type="text" 
          bind:value={userName}
          required
          placeholder={$_('signup.user_name_placeholder')}
        />
      </div>
      
      {#if !isTeamInvitation} 
      {@const sportsTypes = [
        {label: $_('sports.field_hockey'), value: 'hockey'}, 
        {label: $_('sports.football'), value: 'football'}
      ]}
        <!-- Sports Type -->
        <div>
          <label for="sportsType">{$_('signup.sports_type')}</label>
          <select 
            id="sportsType"
            value={sportsType}
            on:change={onSelectSport}
            required
          >
            {#each sportsTypes as sport}
              <option value={sport.value}>{sport.label}</option>
            {/each}
          </select>
        </div>
        
        <!-- Club Selection with SearchableSelect -->
        <div>
          <label for="clubId">{$_('signup.club')}</label>
          <SearchableSelect 
            id="clubId"
            options={clubs.map(club => ({ value: club.id, label: club.name }))}
            value={clubId}
            on:change={onSelectClub}
            placeholder={$_('signup.select_club')}
          />
        </div>
        
        <!-- Team Name -->
        <div>
          <label for="teamName">{$_('signup.team_name')}</label>
          <input 
            id="teamName"
            type="text" 
            bind:value={teamName} 
            required
            placeholder={$_('signup.team_name_placeholder')}
          />
        </div>
        <p class="text-sm text-italic">{$_('signup.team_name_note')}</p>
      {/if}

      <!-- Terms and Conditions -->
      <div class="mt-6 mb-6">
        <label>
          <input type="checkbox" bind:checked={termsAccepted}>
          <span>{$_('signup.terms_and_conditions')} <a href="https://pankla.com/algemenevoorwaarden" target="_blank">{$_('signup.terms_and_conditions_text')}</a></span>
        </label>
      </div>
      
      <!-- Submit Button -->
      <button 
          type="submit" 
          disabled={isLoading}
      >
          {isLoading ? $_('signup.creating_account') : $_('signup.create_account')}
      </button>
    </form>
  {:else if currentStep === 2 && isInitialized}        
    <!-- Success Message -->
    <div class="card mt-6 mb-6">
      <h1 class="brand-font my-6">{$_('signup.ask_app_title')}</h1> 
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col justify-start mt-6">
          <p class="text-lg">{$_('signup.ask_app_message', { values: { app: $settingsStore.user.messaging_app } })}</p>
          {#if $settingsStore.user.messaging_app === 'whatsapp'}
            <a href="https://wa.me/31610163001" target="_blank" role="button" class=" button-primary">{$_('signup.ask_app_whatsappLink')}</a>
          {:else if $settingsStore.user.messaging_app === 'signal'}
            <a href="sgnl://signal.me" role="button" class="button-primary">{$_('signup.ask_app_signalLink')}</a>
          {/if}
        </div>
        <div class="flex justify-center"> 
          <img class="mobile_preview mt-6" src="/img/mobile_pankla_hello.png" alt="mobile_with_hello_message" >
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .invitation-info {
    padding: 1rem;
    background-color: var(--pico-secondary-background);
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  .invitation-info h3 {
    margin-bottom: 0.5rem;
    color: var(--pico-primary);
  }

  .invitation-info p {
    margin: 0;
    color: var(--pico-text);
  } 

.mobile_preview {
  max-height: 300px;
  width: auto;
  margin: auto;
}

</style>